import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { Patient, LabClient, Panel, Payer, PatientInsurance, TestType, Organization } from '@alpha-edge/shared'
import { organizationSettingsSchema } from '@alpha-edge/shared'
import { useApiEntity } from '../../hooks/use-api-entity'
import { useModal } from '../../hooks/use-modal'
import { api } from '../../services/api'
import {
  PageHeader,
  FormField,
  Alert,
  StatusBadge,
  ConfirmDialog,
  SearchSelect,
  PrintLabelButton,
  btnPrimary,
  btnSecondary,
  btnDanger,
  card,
  input,
  colors,
} from '../../components/ui'
import { OrderDocuments } from './OrderDocuments'
import { InstrumentDataSection } from './InstrumentDataSection'
import { BillingStatusBadge } from '../../components/ui/BillingStatusBadge'

interface OrderForm {
  patientId: string
  patientName?: string
  labClientId: string
  panelId: string
  primaryInsuranceId: string
  secondaryInsuranceId: string
  tertiaryInsuranceId: string
  priority: string
  orderNumber: string
  orderingProviderName: string
  orderingProviderNpi: string
  diagnosisCodes: string
  insuranceAuth: string
  specimenNotes: string
  testTypes: string
  status: string
  cancelReason: string
  billingStatus?: string
  billingStatusUpdatedAt?: string
  billingExternalRef?: string
  panelCompletion?: {
    complete: boolean
    missingRequired: readonly string[]
    optionalPending: readonly string[]
  }
}

const defaults: OrderForm = {
  patientId: '',
  labClientId: '',
  panelId: '',
  primaryInsuranceId: '',
  secondaryInsuranceId: '',
  tertiaryInsuranceId: '',
  priority: 'routine',
  orderNumber: '',
  orderingProviderName: '',
  orderingProviderNpi: '',
  diagnosisCodes: '',
  insuranceAuth: '',
  specimenNotes: '',
  testTypes: '',
  status: 'draft',
  cancelReason: '',
}

const ORDER_TRANSITIONS: Record<string, readonly string[]> = {
  draft: ['submitted', 'canceled'],
  submitted: ['received', 'canceled'],
  received: ['in_progress', 'canceled'],
  in_progress: ['completed', 'canceled'],
  completed: [],
  canceled: [],
}

export function OrderEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const entity = useApiEntity<OrderForm>({
    path: '/orders',
    listPath: '/orders',
    defaultValue: defaults,
  })
  const { data, isNew, loading, saving, error, success, save, cancel, update, setError, refresh } = entity
  const [actionLoading, setActionLoading] = useState(false)
  const [patientDisplay, setPatientDisplay] = useState('')
  const [labClients, setLabClients] = useState<readonly LabClient[]>([])
  const [panels, setPanels] = useState<readonly Panel[]>([])
  const [payers, setPayers] = useState<readonly Payer[]>([])
  const [insurances, setInsurances] = useState<readonly PatientInsurance[]>([])
  const [testTypes, setTestTypes] = useState<readonly TestType[]>([])
  const [showSecondaryInsurance, setShowSecondaryInsurance] = useState(false)
  const [showTertiaryInsurance, setShowTertiaryInsurance] = useState(false)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)
  // §7.6.21 Slice 2 -- org-level order-form field visibility + phone
  // country default. `orderFormFields[key] === false` hides that
  // optional field; absent (or `true`) leaves it visible.
  const [orderFormFields, setOrderFormFields] = useState<Record<string, boolean>>({})
  const isFieldVisible = (key: string) => orderFormFields[key] !== false

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get<readonly LabClient[]>('/lab-clients?isActive=true&limit=100').catch(() => ({ data: [] })),
      api.get<readonly Panel[]>('/panels?isActive=true&limit=100').catch(() => ({ data: [] })),
      api.get<readonly Payer[]>('/payers?isActive=true&limit=100').catch(() => ({ data: [] })),
      api.get<readonly TestType[]>('/test-types?isActive=true&limit=500').catch(() => ({ data: [] })),
      api.get<Organization>('/organizations/me').catch(() => ({ data: null })),
    ]).then(([lcRes, pRes, payerRes, ttRes, orgRes]) => {
      if (cancelled) return
      if (lcRes.data) setLabClients(lcRes.data)
      if (pRes.data) setPanels(pRes.data)
      if (payerRes.data) setPayers(payerRes.data)
      if (ttRes.data) setTestTypes(ttRes.data)
      if (orgRes.data?.settings) {
        const parsed = organizationSettingsSchema.safeParse(orgRes.data.settings)
        if (parsed.success) setOrderFormFields(parsed.data.orderFormFields)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Phase 5.8.5: when patient is known, pull their insurance policies
  // so the order form can offer them as primary/secondary/tertiary
  // selections rather than free-text.
  useEffect(() => {
    if (!data.patientId) {
      setInsurances([])
      return
    }
    let cancelled = false
    api
      .get<readonly PatientInsurance[]>(
        `/patient-insurance?patientId=${encodeURIComponent(data.patientId)}&isActive=true&limit=10`,
      )
      .then((res) => {
        if (cancelled) return
        if (res.data) setInsurances(res.data)
      })
      .catch(() => {
        // non-fatal -- empty list just disables the selector
      })
    return () => {
      cancelled = true
    }
  }, [data.patientId])

  // If the loaded order has secondary/tertiary insurance set, expand
  // those rows automatically so the user can see what's persisted.
  useEffect(() => {
    if (data.secondaryInsuranceId) setShowSecondaryInsurance(true)
    if (data.tertiaryInsuranceId) setShowTertiaryInsurance(true)
  }, [data.secondaryInsuranceId, data.tertiaryInsuranceId])

  const searchPatients = useCallback(async (query: string): Promise<readonly Patient[]> => {
    try {
      const result = await api.get<readonly Patient[]>(`/patients?search=${encodeURIComponent(query)}&limit=10`)
      return result.data ?? []
    } catch {
      return []
    }
  }, [])

  const handlePatientSelect = (patient: Patient) => {
    update('patientId', patient.id)
    setPatientDisplay(`${patient.lastName}, ${patient.firstName} (${patient.medicalRecordNumber || 'No MRN'})`)
  }

  // Phase 5.7.1: picking a lab client auto-fills the order form with
  // that client's configured defaults. Only applies to new orders and
  // only overwrites empty fields so in-progress edits aren't clobbered.
  const handleLabClientChange = (labClientId: string) => {
    update('labClientId', labClientId)
    if (!isNew || !labClientId) return
    const lc = labClients.find((c) => c.id === labClientId)
    if (!lc) return
    if (!data.panelId && lc.defaultPanelIds.length > 0) {
      update('panelId', lc.defaultPanelIds[0])
    }
    if (!data.testTypes && lc.defaultTestTypeIds.length > 0) {
      const codes = lc.defaultTestTypeIds
        .map((id) => testTypes.find((t) => t.id === id)?.code)
        .filter((c): c is string => Boolean(c))
      if (codes.length > 0) update('testTypes', codes.join(', '))
    }
  }

  const handleSave = async () => {
    if (isNew) {
      const testTypes = data.testTypes
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
      const diagnosisCodes = data.diagnosisCodes
        ? data.diagnosisCodes
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
        : undefined
      // Phase 5.8.5: when a panel is picked the testTypes free-text
      // field can be empty; backend fan-out reads from panel.testTypeIds.
      // When omitted, drop the field entirely so Zod's `panelId OR
      // testTypes` refine passes.
      const payload: Record<string, unknown> = {
        ...data,
        diagnosisCodes,
      }
      if (testTypes.length > 0) payload.testTypes = testTypes
      else delete (payload as Record<string, unknown>).testTypes
      if (!data.panelId) delete (payload as Record<string, unknown>).panelId
      if (!data.primaryInsuranceId) delete (payload as Record<string, unknown>).primaryInsuranceId
      if (!data.secondaryInsuranceId) delete (payload as Record<string, unknown>).secondaryInsuranceId
      if (!data.tertiaryInsuranceId) delete (payload as Record<string, unknown>).tertiaryInsuranceId
      entity.setData({ ...data, ...(payload as Partial<OrderForm>) })
    }
    await save()
  }

  const updateStatus = async (status: string) => {
    setActionLoading(true)
    try {
      const body: Record<string, string> = { status }
      if (status === 'canceled') body.cancelReason = data.cancelReason || 'Canceled'
      await api.put(`/orders/${id}`, body)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update')
    }
    setActionLoading(false)
  }

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/orders/${id}`)
      navigate('/orders')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive order')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  const nextStatuses = !isNew ? (ORDER_TRANSITIONS[data.status] ?? []) : []

  return (
    <div>
      <PageHeader
        title={isNew ? 'New Order' : `Order: ${data.orderNumber || 'Draft'}`}
        backTo="/orders"
        backLabel="Back to Orders"
        actions={
          <>
            {!isNew && id && <PrintLabelButton orderId={id} onError={(e) => setError(e.message)} />}
            {!isNew && (
              <button onClick={archiveModal.open} style={btnDanger}>
                Archive
              </button>
            )}
            <button onClick={cancel} style={btnSecondary}>
              Cancel
            </button>
            {(isNew || data.status === 'draft') && (
              <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ display: 'grid', gridTemplateColumns: !isNew ? '2fr 1fr' : '1fr', gap: '1rem' }}>
        <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <FormField name="patientId" label="Patient" required>
            {isNew ? (
              <SearchSelect<Patient>
                placeholder="Search patients by name or MRN..."
                value={data.patientId}
                displayValue={patientDisplay}
                onSearch={searchPatients}
                onSelect={handlePatientSelect}
                renderItem={(p) => (
                  <span>
                    <strong>
                      {p.lastName}, {p.firstName}
                    </strong>
                    <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>
                      {p.medicalRecordNumber || 'No MRN'}
                    </span>
                  </span>
                )}
                keyField="id"
              />
            ) : (
              <input style={input} value={patientDisplay || data.patientName || data.patientId} disabled />
            )}
          </FormField>
          <FormField name="priority" label="Priority">
            <select
              style={input}
              value={data.priority}
              onChange={(e) => update('priority', e.target.value)}
              disabled={!isNew && data.status !== 'draft'}
            >
              <option value="routine">Routine</option>
              <option value="stat">STAT</option>
              <option value="asap">ASAP</option>
            </select>
          </FormField>
          <FormField name="labClientId" label="Lab Client">
            <select
              style={input}
              value={data.labClientId}
              onChange={(e) => handleLabClientChange(e.target.value)}
              disabled={!isNew && data.status !== 'draft'}
            >
              <option value="">-- None --</option>
              {labClients.map((lc) => (
                <option key={lc.id} value={lc.id}>
                  {lc.code} - {lc.name} ({lc.barcodePrefix})
                </option>
              ))}
            </select>
          </FormField>
          <FormField name="panelId" label="Panel (auto-fills tests)">
            <select
              style={input}
              value={data.panelId}
              onChange={(e) => update('panelId', e.target.value)}
              disabled={!isNew && data.status !== 'draft'}
            >
              <option value="">-- None (use Test Types below) --</option>
              {panels.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} - {p.name} ({p.testTypeIds.length} tests)
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            name="orderNumber"
            label="Order Number"
            value={data.orderNumber}
            onChange={(v) => update('orderNumber', v)}
            disabled={!isNew}
          />
          <FormField
            name="orderingProviderName"
            label="Ordering Provider"
            value={data.orderingProviderName}
            onChange={(v) => update('orderingProviderName', v)}
          />
          {isFieldVisible('orderingProviderNpi') && (
            <FormField
              name="orderingProviderNpi"
              label="Provider NPI"
              value={data.orderingProviderNpi}
              onChange={(v) => update('orderingProviderNpi', v)}
            />
          )}
          {isFieldVisible('insuranceAuth') && (
            <FormField
              name="insuranceAuth"
              label="Insurance Auth #"
              value={data.insuranceAuth}
              onChange={(v) => update('insuranceAuth', v)}
            />
          )}
          <FormField name="primaryInsuranceId" label="Primary Insurance">
            <select
              style={input}
              value={data.primaryInsuranceId}
              onChange={(e) => update('primaryInsuranceId', e.target.value)}
              disabled={!isNew && data.status !== 'draft'}
            >
              <option value="">-- None --</option>
              {insurances.map((ins) => {
                const payer = payers.find((p) => p.id === ins.payerId)
                return (
                  <option key={ins.id} value={ins.id}>
                    {(payer?.name ?? 'Unknown payer') + ' / ' + ins.memberId}
                  </option>
                )
              })}
            </select>
          </FormField>
          <div style={{ gridColumn: '1 / -1' }}>
            {showSecondaryInsurance ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'end' }}>
                <FormField name="secondaryInsuranceId" label="Secondary Insurance">
                  <select
                    style={input}
                    value={data.secondaryInsuranceId}
                    onChange={(e) => update('secondaryInsuranceId', e.target.value)}
                    disabled={!isNew && data.status !== 'draft'}
                  >
                    <option value="">-- None --</option>
                    {insurances.map((ins) => {
                      const payer = payers.find((p) => p.id === ins.payerId)
                      return (
                        <option key={ins.id} value={ins.id}>
                          {(payer?.name ?? 'Unknown payer') + ' / ' + ins.memberId}
                        </option>
                      )
                    })}
                  </select>
                </FormField>
                <button
                  type="button"
                  style={btnSecondary}
                  onClick={() => {
                    update('secondaryInsuranceId', '')
                    setShowSecondaryInsurance(false)
                    if (showTertiaryInsurance) {
                      update('tertiaryInsuranceId', '')
                      setShowTertiaryInsurance(false)
                    }
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button type="button" style={btnSecondary} onClick={() => setShowSecondaryInsurance(true)}>
                + Add secondary insurance
              </button>
            )}
          </div>
          {showSecondaryInsurance && (
            <div style={{ gridColumn: '1 / -1' }}>
              {showTertiaryInsurance ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'end' }}>
                  <FormField name="tertiaryInsuranceId" label="Tertiary Insurance">
                    <select
                      style={input}
                      value={data.tertiaryInsuranceId}
                      onChange={(e) => update('tertiaryInsuranceId', e.target.value)}
                      disabled={!isNew && data.status !== 'draft'}
                    >
                      <option value="">-- None --</option>
                      {insurances.map((ins) => {
                        const payer = payers.find((p) => p.id === ins.payerId)
                        return (
                          <option key={ins.id} value={ins.id}>
                            {(payer?.name ?? 'Unknown payer') + ' / ' + ins.memberId}
                          </option>
                        )
                      })}
                    </select>
                  </FormField>
                  <button
                    type="button"
                    style={btnSecondary}
                    onClick={() => {
                      update('tertiaryInsuranceId', '')
                      setShowTertiaryInsurance(false)
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button type="button" style={btnSecondary} onClick={() => setShowTertiaryInsurance(true)}>
                  + Add tertiary insurance
                </button>
              )}
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              name="testTypes"
              label="Test Types (comma-separated)"
              required
              value={
                isNew
                  ? data.testTypes
                  : Array.isArray(data.testTypes)
                    ? (data.testTypes as unknown as string[]).join(', ')
                    : data.testTypes
              }
              onChange={(v) => update('testTypes', v)}
              disabled={!isNew}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              name="diagnosisCodes"
              label="Diagnosis Codes (comma-separated)"
              value={data.diagnosisCodes}
              onChange={(v) => update('diagnosisCodes', v)}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              name="specimenNotes"
              label="Specimen Notes"
              multiline
              rows={3}
              value={data.specimenNotes}
              onChange={(v) => update('specimenNotes', v)}
            />
          </div>
        </div>

        {!isNew && (
          <div style={card}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Status</h3>
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <StatusBadge status={data.status} />
              {data.panelCompletion &&
                (data.panelCompletion.complete ? (
                  <StatusBadge status="panel_complete" label="Panel complete" />
                ) : (
                  <span title={`Missing required: ${data.panelCompletion.missingRequired.join(', ') || 'none'}`}>
                    <StatusBadge
                      status="panel_incomplete"
                      label={`Awaiting ${data.panelCompletion.missingRequired.length} required`}
                    />
                  </span>
                ))}
            </div>
            {nextStatuses.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {nextStatuses
                  .filter((s) => s !== 'canceled')
                  .map((s) => (
                    <button key={s} onClick={() => updateStatus(s)} disabled={actionLoading} style={btnPrimary}>
                      {s === 'submitted'
                        ? 'Submit'
                        : s === 'received'
                          ? 'Mark Received'
                          : s === 'in_progress'
                            ? 'Start Processing'
                            : s === 'completed'
                              ? 'Complete'
                              : s.replace(/_/g, ' ')}
                    </button>
                  ))}
                {nextStatuses.includes('canceled') && (
                  <>
                    <div style={{ borderTop: `1px solid ${colors.border}`, margin: '0.5rem 0' }} />
                    <input
                      style={input}
                      placeholder="Cancel reason..."
                      value={data.cancelReason}
                      onChange={(e) => update('cancelReason', e.target.value)}
                    />
                    <button onClick={() => updateStatus('canceled')} disabled={actionLoading} style={btnDanger}>
                      Cancel Order
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!isNew && id && <OrderDocuments orderId={id} />}

      {!isNew && id && <InstrumentDataSection orderId={id} />}

      {!isNew && data.billingStatus && data.billingStatus !== 'none' && (
        <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>Billing</h4>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <span style={{ fontSize: 12, color: '#64748b' }}>Status</span>
              <div>
                <BillingStatusBadge status={data.billingStatus} />
              </div>
            </div>
            {data.billingExternalRef && (
              <div>
                <span style={{ fontSize: 12, color: '#64748b' }}>External Ref</span>
                <div style={{ fontSize: 14 }}>{data.billingExternalRef}</div>
              </div>
            )}
            {data.billingStatusUpdatedAt && (
              <div>
                <span style={{ fontSize: 12, color: '#64748b' }}>Last Updated</span>
                <div style={{ fontSize: 14 }}>{new Date(data.billingStatusUpdatedAt).toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Order"
        message="Are you sure you want to archive this order? It will be excluded from search results."
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
